import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Empty, Row, Space, Spin, Statistic, Steps, Tag, Typography } from 'antd';
import {
  Activity,
  Database,
  Globe,
  ShieldCheck,
  ServerCog,
  Signal,
  ArrowUpRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import InternalLayout from './InternalLayout';
import './Dashboard.css';

const { Paragraph, Text } = Typography;

const Dashboard = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch('/api/dashboard/overview', {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });

        if (!response.ok) {
          throw new Error('Unable to load dashboard data');
        }

        const data = await response.json();
        setOverview(data);
      } catch (err) {
        setError(err.message || 'Unable to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, []);

  const trend = useMemo(() => overview?.trend || [], [overview?.trend]);
  const maxEvents = useMemo(() => {
    if (!trend.length) return 1;
    return Math.max(...trend.map(point => point.events), 1);
  }, [trend]);

  if (loading) {
    return (
      <InternalLayout title="Networked Learning Dashboard" description="Unified observability for internal systems.">
        <Card className="dashboard-loading"><Spin /> Loading dashboard...</Card>
      </InternalLayout>
    );
  }

  if (error) {
    return (
      <InternalLayout title="Networked Learning Dashboard" description="Unified observability for internal systems.">
        <Alert type="error" showIcon message={error} />
      </InternalLayout>
    );
  }

  const generatedAt = overview?.generated_at
    ? new Date(overview.generated_at).toLocaleString()
    : '—';

  return (
    <InternalLayout
      title="Networked Learning Dashboard"
      description="One read-only control plane for PING and SPARC Web. Monitor sessions, engagement, and data health without touching production workloads."
      actions={<Tag color="processing"><Signal size={14} /> Unified Observability</Tag>}
    >
      <div className="dashboard-page">
        <Row className="dashboard-overview" gutter={[16, 16]}>
          <Col xs={24} sm={12} xl={6}><Card><Statistic title="Total Apps" value={overview?.totals?.apps ?? 0} /></Card></Col>
          <Col xs={24} sm={12} xl={6}><Card><Statistic title="Users" value={overview?.totals?.users ?? 0} /></Card></Col>
          <Col xs={24} sm={12} xl={6}><Card><Statistic title="Sessions" value={overview?.totals?.sessions ?? 0} /></Card></Col>
          <Col xs={24} sm={12} xl={6}><Card><Statistic title="Events" value={overview?.totals?.events ?? 0} /></Card></Col>
        </Row>

        <Card className="dashboard-meta">
          <Space size={[24, 12]} wrap>
            <Text><strong>Signed in as:</strong> {user?.full_name || user?.email || 'Administrator'}</Text>
            <Text><strong>Read replica:</strong> {overview?.read_replica ? 'Enabled' : 'Primary only'}</Text>
            <Text><strong>Generated:</strong> {generatedAt}</Text>
          </Space>
        </Card>

        <Card
          className="dashboard-pipeline"
          title="Data Pipeline"
          extra={<Tag>Read Only</Tag>}
        >
          <Paragraph>Aligned with the production architecture for safe, read-only analytics.</Paragraph>
          <Steps
            responsive
            items={[
              { title: 'Ingress', description: 'NGINX TLS + routing', icon: <Globe size={18} /> },
              { title: 'Core Data Service', description: 'Single-writer API', icon: <ServerCog size={18} /> },
              { title: 'Primary + Replica', description: 'Write + reporting store', icon: <Database size={18} /> },
              { title: 'Dashboard UI', description: 'Read-only analytics', icon: <Activity size={18} /> },
              { title: 'Observability', description: 'Prometheus + Grafana', icon: <ShieldCheck size={18} /> },
            ]}
          />
        </Card>

        <Card className="trend-card" title="Usage Trend">
          <Paragraph>Event volume over the last two weeks.</Paragraph>
          {trend.length ? (
            <>
              <div className="trend-chart">
                {trend.map((point) => (
                  <div
                    className="trend-bar"
                    key={point.date}
                    title={`${point.date}: ${point.events} events`}
                    style={{ height: `${(point.events / maxEvents) * 100}%` }}
                  />
                ))}
              </div>
              <div className="trend-footer">
                <span>{trend[0].date}</span>
                <span>{trend[trend.length - 1].date}</span>
              </div>
            </>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No trend data yet." />
          )}
        </Card>

        <section className="dashboard-apps">
          <Row gutter={[16, 16]}>
            {(overview?.apps || []).map((app) => (
              <Col xs={24} md={12} xl={8} key={app.slug}>
                <Card
                  className={`app-card ${app.connected ? 'connected' : 'pending'}`}
                  title={app.name}
                  extra={<Tag color={app.connected ? 'success' : 'warning'}>{app.connected ? 'Connected' : 'Pending'}</Tag>}
                >
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Space wrap>
                      <Tag>{app.slug.toUpperCase()}</Tag>
                      <Text type="secondary">{app.base_url || 'No base URL set'}</Text>
                    </Space>
                    <Row gutter={[12, 12]}>
                      <Col span={8}><Statistic title="Users" value={app.users} /></Col>
                      <Col span={8}><Statistic title="Sessions" value={app.sessions} /></Col>
                      <Col span={8}><Statistic title="Events" value={app.events} /></Col>
                    </Row>
                    <div className="app-footer">
                      <div>
                        <span>Last activity</span>
                        <strong>{app.last_event_at ? new Date(app.last_event_at).toLocaleString() : 'No data'}</strong>
                      </div>
                      <ArrowUpRight size={18} />
                    </div>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </section>
      </div>
    </InternalLayout>
  );
};

export default Dashboard;
